import React, { useState } from "react";
import { authService } from "./auth";

// interface LoginScreenProps {
//   onLoginSuccess: (user: User) => void;
// }

const LoginScreen = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState(null);

  // Standard regex for email validation
  const validateEmailFormat = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Initial basic client-side validation
    if (!validateEmailFormat(email)) {
      setError("Please enter a valid email address (e.g., name@example.com).");
      return;
    }

    if (!isLogin && (!name || name.trim().length < 2)) {
      setError("Please enter your full name.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    setLoading(true);
    if (!isLogin) setValidating(true);

    try {
      let user;
      if (isLogin) {
        user = await authService.login(email, password);
      } else {
        // This call now includes Real-World DNS validation
        user = await authService.register(email, password, name);
      }
      onLoginSuccess(user);
    } catch (err) {
      setError(err.message || "Authentication failed.");
    } finally {
      setLoading(false);
      setValidating(false);
    }
  };

  const handleGuestLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await authService.loginAsGuest();
      onLoginSuccess(user);
    } catch (err) {
      setError("Could not enter as guest.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6 relative overflow-hidden">
        {/* Background Orbs */}
        {/* <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-600/20 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-rose-600/10 rounded-full blur-[120px] animate-pulse delay-700"></div> */}

        <div className="max-w-md w-full glass p-10 rounded-[2.5rem] relative z-10 border-white/5 shadow-2xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-2xl mb-6 shadow-xl shadow-indigo-600/30">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-black text-white tracking-tight mb-2">ProHeadshot AI</h1>
            <p className="text-slate-400 text-sm font-medium">
              Professional Studio-Quality AI Portraits
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-4">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-slate-900/50 border border-white/10 rounded-2xl px-5 py-3.5 text-sm text-white focus:border-indigo-500/50 outline-none transition-all"
                  placeholder="John Doe"
                />
              </div>
            )}

            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-4">
                Email Address
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-slate-900/50 border border-white/10 rounded-2xl px-5 py-3.5 text-sm text-white focus:border-indigo-500/50 outline-none transition-all"
                placeholder="name@company.com"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-4">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-900/50 border border-white/10 rounded-2xl px-5 py-3.5 text-sm text-white focus:border-indigo-500/50 outline-none transition-all"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-[11px] font-bold text-center animate-shake">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-600 text-white font-black text-sm uppercase tracking-widest py-4 rounded-2xl shadow-xl shadow-indigo-600/20 transition-all active:scale-[0.98] mt-4 flex items-center justify-center space-x-2`}
            >
              {loading ? (
                <React.Fragment>
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                  <span>{validating ? "Verifying Domain..." : "Authenticating..."}</span>
                </React.Fragment>
              ) : (
                <span>{isLogin ? "Enter Studio" : "Create Account"}</span>
              )}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-white/5 space-y-4">
            <div className="flex items-center justify-between text-xs">
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError(null);
                }}
                className="text-indigo-400 hover:text-indigo-300 font-bold tracking-tight transition-colors"
              >
                {isLogin ? "Don't have an account? Sign Up" : "Already have an account? Log In"}
              </button>
            </div>

            <button
              onClick={handleGuestLogin}
              disabled={loading}
              className="w-full bg-slate-800/50 hover:bg-slate-800 text-slate-400 hover:text-white font-bold text-xs uppercase tracking-widest py-3 rounded-2xl border border-white/5 transition-all"
            >
              Continue as Guest
            </button>
          </div>

          <p className="mt-8 text-[10px] text-slate-600 text-center font-medium leading-relaxed">
            By continuing, you agree to our Terms of Service
            <br />
            and acknowledge our AI Processing Protocols.
          </p>
        </div>
      </div>
    </React.Fragment>
  );
};

export default LoginScreen;
